'use client'

import { useEffect, useRef, useState } from 'react'

interface ParallaxSectionProps {
  children: React.ReactNode
  speed?: number // 0-1, where 0.5 means half speed (default)
  className?: string
}

export default function ParallaxSection({ 
  children, 
  speed = 0.5,
  className = '' 
}: ParallaxSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const rafIdRef = useRef<number | null>(null)
  const lastScrollY = useRef<number>(0)
  const isVisibleRef = useRef<boolean>(false)
  const ticking = useRef<boolean>(false)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted) return

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const section = sectionRef.current
    if (!section) return

    // Use direct DOM manipulation for best performance
    const updateParallax = () => {
      if (!section || !isVisibleRef.current) {
        ticking.current = false
        return
      }

      const rect = section.getBoundingClientRect()
      const windowHeight = window.innerHeight
      
      // Only apply parallax when the section is in or near viewport
      if (rect.bottom < -windowHeight || rect.top > windowHeight * 2) {
        ticking.current = false
        return
      }

      const scrolledPastTop = -rect.top
      const parallaxOffset = scrolledPastTop * speed

      // Direct DOM manipulation - no React re-render
      // Use translate3d for GPU acceleration
      section.style.transform = `translate3d(0, ${parallaxOffset}px, 0)`
      
      ticking.current = false
    }

    // Intersection Observer to only update when section is visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting
          
          // Remove willChange when not visible for better performance
          if (!entry.isIntersecting) {
            if (rafIdRef.current) {
              cancelAnimationFrame(rafIdRef.current)
              rafIdRef.current = null
            }
            if (section) {
              section.style.willChange = 'auto'
            }
          } else {
            if (section) {
              section.style.willChange = 'transform'
            }
            // Trigger immediate update when becoming visible
            requestAnimationFrame(updateParallax)
          }
        })
      },
      {
        // Larger root margin for smoother transitions
        rootMargin: '100px 0px',
        threshold: 0
      }
    )

    observer.observe(section)

    // Optimized scroll handler using requestAnimationFrame with throttling
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Only update if scroll position actually changed
      if (currentScrollY === lastScrollY.current) return
      lastScrollY.current = currentScrollY

      // Use ticking pattern to prevent multiple rAF calls
      if (!ticking.current) {
        ticking.current = true
        rafIdRef.current = requestAnimationFrame(updateParallax)
      }
    }

    // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        if (isVisibleRef.current) {
          updateParallax()
        }
      }, 150)
    }

    // Initial calculation with slight delay to ensure proper layout
    const initTimeout = setTimeout(() => {
      updateParallax()
    }, 100)

    // Add scroll listener with passive flag for better performance
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    // Cleanup
    return () => {
      observer.disconnect()
      clearTimeout(initTimeout)
      clearTimeout(resizeTimeout)
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [speed, isMounted])

  // Prevent hydration mismatch by suppressing hydration warnings
  return (
    <div 
      ref={sectionRef} 
      className={className}
      suppressHydrationWarning
      style={{
        transform: 'translate3d(0, 0, 0)',
        willChange: 'auto', // Start with auto, set to transform only when visible
        height: '130%',
        width: '100%',
        top: '-15%',
        left: 0,
        position: 'absolute',
        backfaceVisibility: 'hidden', // Prevent flickering on some browsers
        WebkitBackfaceVisibility: 'hidden',
        perspective: 1000, // Improve 3D rendering
        WebkitPerspective: 1000,
      }}
    >
      {children}
    </div>
  )
}
